import axios from 'axios'

export const DEFAULT_CURRENCY = 'EUR'

const API_BASE =
  process.env.VUE_APP_API_URL ||
  process.env.VUE_APP_API_ENDPOINT ||
  '/api'
const API_BASE_URL = String(API_BASE).replace(/\/+$/, '')
const IP_INFO_URL =
  process.env.VUE_APP_IP_INFO_URL ||
  `${API_BASE_URL}/ip-info`
const CLIENT_IP_URL = 'https://api.ipify.org'
const IP_INFO_TIMEOUT_MS = 7000
const CLIENT_IP_TIMEOUT_MS = 5000
const GEO_IP_INFO_STORAGE_KEY = 'userIpInfo'
const GEO_CURRENCY_STORAGE_KEY = 'userCurrency'
const GEO_CLIENT_IP_STORAGE_KEY = 'userClientIp'

let userIpInfoPromise = null

function isDev() {
  return process.env.NODE_ENV !== 'production'
}

function debugLog(...args) {
  if (!isDev() || typeof console === 'undefined' || typeof console.debug !== 'function') return
  console.debug('[geo]', ...args)
}

function normalizeCurrencyCode(currencyCode) {
  if (typeof currencyCode !== 'string') return ''
  return currencyCode.trim().toUpperCase()
}

function normalizeString(value) {
  if (typeof value !== 'string') return ''
  return value.trim()
}

function normalizeIp(value) {
  const normalized = normalizeString(value)
  if (!normalized || normalized.length > 64) return ''
  return /^[a-f0-9:.]+$/i.test(normalized) ? normalized : ''
}

function resolveLocalizedName(value) {
  if (!value || typeof value !== 'object') return ''

  const translatedName =
    typeof value.name_translated === 'string' ? value.name_translated.trim() : ''
  if (translatedName) return translatedName

  const fallbackName = typeof value.name === 'string' ? value.name.trim() : ''
  return fallbackName
}

function normalizeIpInfoResponse(payload) {
  if (!payload || typeof payload !== 'object') return null

  const source = payload?.data && typeof payload.data === 'object' ? payload.data : payload
  if (!source || typeof source !== 'object') return null

  const location = source?.location && typeof source.location === 'object' ? source.location : {}
  const country = location?.country && typeof location.country === 'object' ? location.country : {}
  const city = location?.city && typeof location.city === 'object' ? location.city : {}
  const region = location?.region && typeof location.region === 'object' ? location.region : {}
  const timezoneInfo =
    source?.timezone && typeof source.timezone === 'object' ? source.timezone : {}

  const countryCurrencyCode = Array.isArray(country?.currencies)
    ? normalizeCurrencyCode(country.currencies.find((item) => item?.code)?.code)
    : ''
  const currency =
    normalizeCurrencyCode(source?.currency_code) ||
    normalizeCurrencyCode(source?.currencyCode) ||
    normalizeCurrencyCode(source?.currency) ||
    countryCurrencyCode
  const sourceCountry = normalizeString(source?.country)
  const sourceCity = normalizeString(source?.city)
  const sourceRegion = normalizeString(source?.subdivision) || normalizeString(source?.region)
  const sourceContinent = normalizeString(source?.continent)
  const sourceTimezone = normalizeString(source?.time_zone) || normalizeString(source?.timezone)
  const countryCode =
    normalizeString(source?.country_code) ||
    normalizeString(source?.countryCode) ||
    normalizeString(country?.alpha2)
  const callingCode =
    normalizeString(source?.calling_code) ||
    normalizeString(source?.callingCode) ||
    normalizeString(source?.phone_code) ||
    normalizeString(source?.phoneCode)

  return {
    ...source,
    currency,
    currencyCode: currency,
    country: sourceCountry || resolveLocalizedName(country),
    countryCode,
    country_code: countryCode,
    continent: sourceContinent || resolveLocalizedName(location?.continent),
    continentCode: normalizeString(source?.continentCode) || normalizeString(location?.continent?.code),
    city: sourceCity || resolveLocalizedName(city),
    region: sourceRegion || resolveLocalizedName(region),
    regionCode: normalizeString(source?.regionCode) || normalizeString(region?.alpha2),
    zip: normalizeString(source?.postal_code) || normalizeString(source?.zip) || normalizeString(location?.zip),
    latitude: source?.latitude ?? location?.latitude ?? null,
    longitude: source?.longitude ?? location?.longitude ?? null,
    timezone: sourceTimezone || normalizeString(timezoneInfo?.id),
    time_zone: sourceTimezone || normalizeString(timezoneInfo?.id),
    timezoneCode: normalizeString(source?.timezoneCode) || normalizeString(timezoneInfo?.code),
    timezoneOffset: source?.timezoneOffset ?? timezoneInfo?.gmt_offset ?? null,
    calling_code: callingCode,
    callingCode,
    phone_code: callingCode,
    phoneCode: callingCode,
    timezoneData: timezoneInfo,
    locationData: location,
    rawData: source,
  }
}

function getSessionStorage() {
  if (typeof window === 'undefined' || !window.sessionStorage) return null
  return window.sessionStorage
}

function getStoredIpInfo() {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage) return null

  try {
    const raw = sessionStorage.getItem(GEO_IP_INFO_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (error) {
    return null
  }
}

function setStoredIpInfo(ipInfo) {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage || !ipInfo || typeof ipInfo !== 'object') return

  try {
    sessionStorage.setItem(GEO_IP_INFO_STORAGE_KEY, JSON.stringify(ipInfo))
  } catch (error) {
    // noop
  }
}

function getStoredClientIp() {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage) return ''

  try {
    const directIp = sessionStorage.getItem(GEO_CLIENT_IP_STORAGE_KEY)
    if (normalizeIp(directIp)) return normalizeIp(directIp)

    const storedIpInfo = getStoredIpInfo()
    const infoIp = normalizeIp(storedIpInfo?.ip)
    return infoIp
  } catch (error) {
    return ''
  }
}

function getStoredCurrency() {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage) return ''

  try {
    return normalizeCurrencyCode(sessionStorage.getItem(GEO_CURRENCY_STORAGE_KEY))
  } catch (error) {
    return ''
  }
}

function getCompleteStoredIpInfo() {
  const storedIpInfo = getStoredIpInfo()
  const storedClientIp = getStoredClientIp()
  const storedCurrency = getStoredCurrency()

  if (!storedIpInfo || !storedClientIp || !storedCurrency) return null

  return {
    ...storedIpInfo,
    ip: normalizeIp(storedIpInfo.ip) || storedClientIp,
    currency: normalizeCurrencyCode(storedIpInfo.currency) || storedCurrency,
    currencyCode: normalizeCurrencyCode(storedIpInfo.currencyCode) || storedCurrency,
  }
}

function setStoredClientIp(ip) {
  const sessionStorage = getSessionStorage()
  if (!sessionStorage) return
  const normalizedIp = normalizeIp(ip)
  if (!normalizedIp) return

  try {
    sessionStorage.setItem(GEO_CLIENT_IP_STORAGE_KEY, normalizedIp)
  } catch (error) {
    // noop
  }
}

function setStoredCurrency(currencyCode) {
  const normalizedCurrencyCode = normalizeCurrencyCode(currencyCode)
  const sessionStorage = getSessionStorage()
  if (!sessionStorage || !normalizedCurrencyCode) return

  try {
    sessionStorage.setItem(GEO_CURRENCY_STORAGE_KEY, normalizedCurrencyCode)
  } catch (error) {
    // noop
  }
}

async function fetchIpInfoFromBackend(ip) {
  const normalizedIp = normalizeIp(ip)
  const headers = {
    'Content-Type': 'application/json',
  }

  if (normalizedIp) {
    headers['X-Client-IP'] = normalizedIp
  }

  const { data } = await axios.get(IP_INFO_URL, {
    timeout: IP_INFO_TIMEOUT_MS,
    headers,
    params: normalizedIp ? { ip: normalizedIp } : {},
  })

  return normalizeIpInfoResponse(data)
}

async function fetchClientIpFromUrl(url) {
  if (typeof fetch !== 'function') return ''

  const timeout = new Promise((resolve) => {
    window.setTimeout(() => resolve(''), CLIENT_IP_TIMEOUT_MS)
  })
  const request = fetch(url)
    .then((response) => (response.ok ? response.text() : ''))
    .then((body) => {
      const raw = normalizeString(body)
      if (!raw) return ''

      try {
        const parsed = JSON.parse(raw)
        return normalizeIp(parsed?.ip)
      } catch (error) {
        return normalizeIp(raw)
      }
    })

  return Promise.race([request, timeout])
}

async function fetchClientIp() {
  try {
    const ip = await fetchClientIpFromUrl(CLIENT_IP_URL)
    if (ip) return ip
  } catch (error) {
    debugLog('Client IP lookup failed')
  }

  return ''
}

async function fetchUserIpInfo() {
  if (typeof window === 'undefined') return null

  const completeStoredIpInfo = getCompleteStoredIpInfo()
  if (completeStoredIpInfo) {
    debugLog('Using complete cached ip data response from sessionStorage')
    return completeStoredIpInfo
  }

  const clientIp = await fetchClientIp()
  const resolvedIp = clientIp || getStoredClientIp()

  try {
    const ipInfo = await fetchIpInfoFromBackend(resolvedIp)
    if (!ipInfo) return null

    setStoredIpInfo(ipInfo)
    setStoredClientIp(resolvedIp || ipInfo.ip)
    setStoredCurrency(ipInfo.currency)
    debugLog('Fetched ip data from backend', {
      hasIp: Boolean(ipInfo.ip),
      forwardedClientIp: Boolean(resolvedIp),
    })
    return ipInfo
  } catch (error) {
    debugLog('IP data request failed; continuing without geo data')
    return null
  }
}

export async function getUserIpInfo() {
  if (!userIpInfoPromise) {
    userIpInfoPromise = fetchUserIpInfo()
  }

  return userIpInfoPromise
}

export async function getUserGeoCurrencyContext() {
  const ipInfo = await getUserIpInfo()
  const currency = normalizeCurrencyCode(ipInfo?.currency) || DEFAULT_CURRENCY
  return { currency }
}

export async function getUserCurrencyByIp() {
  const context = await getUserGeoCurrencyContext()
  return context.currency || DEFAULT_CURRENCY
}
